// El vocabulario del semáforo de material, en un solo sitio.
//
// Existe porque el mismo semáforo se pinta en DOS pantallas que llegan a él por
// caminos distintos: Secuencia lo calcula en el navegador —porque es un
// simulador y el estado depende del orden en que se produzca— y Gantt lo lee ya
// calculado de `dbo.tabla_planificacion`. Dos caminos y una sola regla; si la
// regla se escribiera dos veces, las dos pantallas acabarían discrepando sobre
// la misma orden y nadie sabría cuál creerse.
import type { ComponentStatus } from '@/types/productionSequence'

/**
 * De menos a más grave.
 *
 * El orden no es decorativo: es lo que decide qué se enseña cuando una orden
 * tiene componentes en estados distintos. Una orden con nueve componentes en
 * Gavà y uno sin comprar no está «casi bien» — no se puede producir, y el
 * semáforo tiene que decir eso.
 */
export const GRAVEDAD: Record<ComponentStatus, number> = {
  OK: 0,
  // Entre OK y ENTRE_ALMACENES: la cascada del notebook lo evalúa antes que
  // ENTRE_ALMACENES. Hay material, falta un trámite de calidad.
  PENDIENTE_CALIDAD: 1,
  ENTRE_ALMACENES: 2,
  COMPRAS_EN_CURSO: 3,
  ROTURA_STOCK: 4,
}

/**
 * El peor estado de un conjunto: lo que se pinta en la fila de la orden cuando
 * por debajo hay N componentes.
 *
 * Sin componentes devuelve `OK`, que es lo que ya hacía Secuencia: una orden sin
 * material que vigilar no tiene nada que la frene.
 */
export function peorEstado(estados: readonly ComponentStatus[]): ComponentStatus {
  let peor: ComponentStatus = 'OK'
  for (const e of estados) if (GRAVEDAD[e] > GRAVEDAD[peor]) peor = e
  return peor
}

/** ¿Existe el material, esté donde esté? Verde, violeta (en calidad) y azul.
 *  El naranja depende de que el proveedor cumpla. */
export function hayMaterial(estado: ComponentStatus): boolean {
  return estado === 'OK' || estado === 'PENDIENTE_CALIDAD' || estado === 'ENTRE_ALMACENES'
}

/**
 * ¿Cuenta esta fila de componente como RIESGO?
 *
 * `flag_rotura` viene a 1 también en las filas PENDIENTE_CALIDAD (el material
 * existe pero no es utilizable aún), y contarlas haría que la fila dijera a la
 * vez «hay material» (chip) y «en riesgo» (fondo, ⚠N, alertas).
 *
 * Un estado ausente o desconocido NO es calidad: sólo se excluye lo que es
 * PENDIENTE_CALIDAD sin ambigüedad (equivalente al COALESCE del SQL del
 * dashboard). Hoy la columna puede no existir y llegar `undefined`.
 */
export function cuentaComoRotura(
  r: { flagRotura: number; estadoMaterial?: string | null },
): boolean {
  return r.flagRotura === 1 && asEstadoMaterial(r.estadoMaterial) !== 'PENDIENTE_CALIDAD'
}

/** Los cinco, para recorrerlos sin escribirlos a mano cada vez. */
export const ESTADOS_MATERIAL = [
  'OK', 'PENDIENTE_CALIDAD', 'ENTRE_ALMACENES', 'COMPRAS_EN_CURSO', 'ROTURA_STOCK',
] as const satisfies readonly ComponentStatus[]

/**
 * Estrecha el texto que llega de la tabla precalculada.
 *
 * El estado viaja como cadena desde un notebook que no está en este repo. Si
 * algún día emite un valor que no conocemos, esto devuelve `undefined` y la fila
 * se pinta sin semáforo: peor información, pero pantalla viva. Confiar en la
 * cadena y usarla como clave daría un color en blanco, o algo peor si se usa
 * para decidir.
 */
export function asEstadoMaterial(v: string | null | undefined): ComponentStatus | undefined {
  const s = (v ?? '').trim().toUpperCase()
  return (ESTADOS_MATERIAL as readonly string[]).includes(s)
    ? (s as ComponentStatus)
    : undefined
}

/**
 * ¿Qué significa la fecha que acompaña a este estado?
 *
 * En OK y ENTRE_ALMACENES el material ya está: la fecha es una recepción REAL,
 * pasada. En COMPRAS_EN_CURSO y ROTURA_STOCK todavía no está: la fecha es una
 * PREVISIÓN. Poner «llegó» y «llegará» en la misma columna sin distinguirlas es
 * la manera de que alguien planifique contra una fecha que aún no ha ocurrido.
 */
export function fechaEsPrevision(estado: ComponentStatus): boolean {
  return estado === 'COMPRAS_EN_CURSO' || estado === 'ROTURA_STOCK'
}

/** Los tres tiempos verbales que puede tener la fecha de un estado. */
export type TiempoFecha = 'llego' | 'llegara' | 'vencio'

/**
 * Qué tiempo verbal le corresponde a la fecha de un estado.
 *
 * No basta con mirar el estado. Una ROTURA puede traer la fecha de una compra
 * **vencida** —la que debía haber llegado y no llegó—, y rotularla «llegada
 * prevista» sería una promesa de futuro sobre una fecha que ya pasó. Es lo que
 * pasa desde que las entregas vencidas dejaron de contar como suministro: su
 * fecha se sigue enseñando, para que el rojo sea accionable en vez de un muro,
 * pero significa lo contrario de una previsión.
 *
 * `vencio` es lo que convierte «no vas a poder producir» en «la compra debía
 * haber llegado hace 58 días»: lo primero es un callejón, lo segundo es una
 * llamada al proveedor.
 */
export function tiempoDeFecha(
  estado: ComponentStatus,
  fecha: string,
  hoy: string = new Date().toISOString().slice(0, 10),
): TiempoFecha {
  if (!fechaEsPrevision(estado)) return 'llego'
  return fecha.slice(0, 10) < hoy.slice(0, 10) ? 'vencio' : 'llegara'
}
